import Vue from 'vue';
import { aiModulesService } from '@/services/ai-modules.service';
import { opportunityDocumentsService } from '@/services/opportunity-documents.service';
import { opportunityLlmExtractionsService } from '@/services/opportunity-llm-extractions.service';
import { followLlmJob } from '@/util/llm-job-stream';

// AbortControllers for in-flight fill job SSE streams, one per job
// (non-serializable, kept outside state). Owned here — separate from the
// extraction watcher's controller — so fills keep streaming while the user
// navigates to pages that watch extraction jobs. Keyed like `fillJobs`: an
// opportunity can have several jobs running at once (one per "Fill" click),
// and starting another must not abort the streams of the ones before it.
const fillControllers = new Map();

// Register a controller for a job key, aborting any stream already under that key.
const newFillController = (jobKey) => {
  const existing = fillControllers.get(jobKey);
  if (existing) existing.abort();
  const controller = new AbortController();
  fillControllers.set(jobKey, controller);
  return controller;
};

const clearFillController = (jobKey) => {
  const controller = fillControllers.get(jobKey);
  if (!controller) return;
  controller.abort();
  fillControllers.delete(jobKey);
};

// A job is tracked under a provisional key until the POST returns its id.
let provisionalJobSeq = 0;
const provisionalJobKey = () => `pending-${Date.now()}-${(provisionalJobSeq += 1)}`;

const clearAllFillControllers = () => {
  fillControllers.forEach((controller) => controller.abort());
  fillControllers.clear();
};

const initialState = () => ({
  forms: [],
  // Documents currently being filled
  filling: {},
  // Latest SSE step of the running fill job (drives the step progress UI).
  fillStep: null,
  // Per-opportunity fill session meta: { [opportunityId]: { opportunityName } }.
  // Present while at least one job of the opportunity is tracked in `fillJobs`.
  fillSessions: {},
  // Running fill jobs: { [jobKey]: { jobId, opportunityId, formIds, completed, total } }.
  // The counts come from the job's own SSE `progress` events, so they are per
  // job; the getters add up the jobs of an opportunity for the page and header.
  fillJobs: {},
  // Form-fill records from the document service, keyed in `formFillByDocument`.
  formFills: [],
  // Per-opportunity persisted template choice: { [opportunityId]: { templateId } }.
  opportunityTemplates: {},
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const findForm = (state, formId) => state.forms.find((f) => f.id === formId);

// Whether `opportunityId` is the opportunity currently loaded in the
// `opportunity` store (i.e. the one whose pages the user is looking at).
// Background fills tracked from the app shell must not refresh that shared
// page state — `formFills` and `opportunity/getOpportunity` are scoped to the
// current opportunity, and the page refetches both on entry anyway.
const isCurrentOpportunity = (rootGetters, opportunityId) => {
  const opportunity = rootGetters['opportunity/opportunity'];
  return !!opportunity && opportunity.id === opportunityId;
};

// Job fields recoverable from a job record when re-attaching: the total is
// the job's requested document count (`documents_used`, same list the SSE
// `progress` events count against). Live/replayed progress events then merge
// the `completed` count in — but they only fire as documents finish, so
// without this seed a re-attached job would show 0/0 until then.
const jobFromRecord = (job) => {
  const documentsUsed = Array.isArray(job.documents_used) ? job.documents_used : [];
  return {
    jobId: job.id,
    opportunityId: job.opportunity_id,
    // `documents_used` is [{ id, name }]: the forms this job still owns until
    // its progress events release them one by one.
    formIds: documentsUsed.map((document) => document && document.id).filter(Boolean),
    completed: 0,
    total: documentsUsed.length,
  };
};

const isJobRunning = (job) => !!job && (job.status === 'PENDING' || job.status === 'PROCESSING');

const jobsOf = (state, opportunityId) =>
  Object.keys(state.fillJobs)
    .map((key) => state.fillJobs[key])
    .filter((job) => job.opportunityId === opportunityId);

// A form's "filling" flag belongs to whichever tracked jobs still own it: a
// job releases a form on its `progress` event, and the user may refill that
// form as a new job while the first one is still running. The flag is only
// cleared once no job owns the form any more.
const ownedByAnyJob = (state, formId) =>
  Object.keys(state.fillJobs).some((key) => (state.fillJobs[key].formIds || []).includes(formId));

// SSE callbacks for a fill job's stream, dispatching into this module's
// actions. On top of the shared `status`/`complete`/`error` events, fill jobs
// emit per-document `progress` events (completed/total counts).
const fillStreamCallbacks = ({ dispatch, opportunityId, jobId, signal }) => {
  const guard = () => !signal.aborted;
  return {
    onStatus(data) {
      if (guard()) dispatch('onProgress', { ssePayload: data });
    },
    onProgress(data) {
      if (guard()) dispatch('onFillProgress', { opportunityId, jobId, ssePayload: data });
    },
    onComplete() {
      if (guard()) dispatch('onJobEnd', { opportunityId, jobKey: jobId });
    },
    onError(err) {
      if (guard()) dispatch('onJobEnd', { opportunityId, jobKey: jobId, error: err });
    },
    onCancelled() {
      if (guard()) dispatch('onJobEnd', { opportunityId, jobKey: jobId });
    },
  };
};

const actions = {
  uploadForms({ commit, dispatch }, { opportunityId, files = [] }) {
    if (!opportunityId || !files.length) return Promise.resolve(false);
    const formData = new FormData();
    files.forEach((file) => {
      if (file) formData.append('file[]', file);
    });
    return opportunityDocumentsService.addDocuments({ opportunityId, formData, type: 'FORM' }).then(
      (documents) => {
        // Keep the opportunities list state in sync (document count on the
        // opportunities page), mirroring opportunityDocuments/addDocuments.
        commit('opportunities/SUCCESS_ADDING_DOCUMENTS', { opportunityId, documents: documents || [] }, { root: true });
        commit(
          'tenderOpportunities/SUCCESS_ADDING_DOCUMENTS',
          { opportunityId, documents: documents || [] },
          { root: true },
        );
        return dispatch('opportunity/getOpportunity', { id: opportunityId }, { root: true }).then(() => true);
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  getDocumentForms({ commit }, opportunityId) {
    return opportunityDocumentsService.getDocumentForms({ opportunityId }).then(
      (formFills) => commit('SET_FORM_FILLS', formFills || []),
      () => commit('SET_FORM_FILLS', []),
    );
  },
  // Mirror of extractionJob/watchExtractionJob for job type `form_filling`:
  // on page (re)entry, ask the backend for the latest fill job and — if it is
  // still running — re-attach to its SSE stream so the progress state survives
  // navigation and full reloads.
  watchFillJob({ commit, dispatch, state }, { opportunityId } = {}) {
    if (!opportunityId) return Promise.resolve();

    return Promise.all(
      ['PENDING', 'PROCESSING'].map((status) =>
        opportunityLlmExtractionsService.getJobs({ opportunityId, jobType: 'form_filling', status }).catch(() => []),
      ),
    ).then((results) => {
      // Every running job of the opportunity gets its own stream; one already
      // followed (in-app navigation) is left alone. Endings are caught per job,
      // by its stream or its own poll.
      const untracked = results.flat().filter((job) => job && job.id && !state.fillJobs[job.id]);
      if (!untracked.length && !jobsOf(state, opportunityId).length) commit('SET_FILL_STEP', null);
      return Promise.all(untracked.map((job) => dispatch('trackJob', { job, opportunityId })));
    });
  },
  // Track a running job from its record and follow its stream.
  trackJob({ commit, dispatch, rootGetters }, { job, opportunityId }) {
    const opportunity = rootGetters['opportunity/opportunity'];
    const opportunityName =
      job.opportunity_name || (opportunity && opportunity.id === opportunityId ? opportunity.name : null);
    commit('SET_FILL_SESSION', { opportunityId, ...(opportunityName ? { opportunityName } : {}) });
    const record = jobFromRecord(job);
    record.formIds.forEach((formId) => commit('SET_FILLING', { formId, value: true }));
    commit('SET_FILL_JOB', { jobKey: job.id, ...record, opportunityId });
    const { signal } = newFillController(job.id);
    return dispatch('subscribeToFillJob', { opportunityId, jobId: job.id, signal });
  },
  // App-shell counterpart of `watchFillJob` for when no page knows which
  // opportunity is filling (e.g. a reload outside the forms page): list the
  // user's running form-fill jobs and re-attach to each, so the header's
  // task pills survive a refresh anywhere. The `status` param is
  // single-valued, hence one query per running state.
  watchActiveFillJobs({ dispatch, state }) {
    return Promise.all(
      ['PENDING', 'PROCESSING'].map((status) =>
        opportunityLlmExtractionsService.getJobs({ jobType: 'form_filling', status }).catch(() => []),
      ),
    ).then((results) =>
      // Every running job gets its own stream; one already followed is left alone.
      Promise.all(
        results
          .flat()
          .filter((job) => job && job.id && job.opportunity_id && !state.fillJobs[job.id])
          .map((job) => dispatch('trackJob', { job, opportunityId: job.opportunity_id })),
      ),
    );
  },
  // Fill one or more form documents via the AI modules service. The POST queues
  // a job we then follow via its SSE stream (with the poll fallback);
  // `watchFillJob` re-attaches to it on page re-entry.
  // Each call is one job, tracked on its own: filling a second form while the
  // first is still running adds to the opportunity's counts instead of
  // replacing them.
  startFill({ commit, dispatch, state, rootGetters }, { formIds = [], templateId = null, opportunityId = null }) {
    if (!formIds.length || !templateId || !opportunityId) return Promise.resolve(false);

    const opportunity = rootGetters['opportunity/opportunity'];
    const opportunityName = opportunity && opportunity.id === opportunityId ? opportunity.name : null;

    const provisionalKey = provisionalJobKey();
    const { signal } = newFillController(provisionalKey);

    formIds.forEach((id) => commit('SET_FILLING', { formId: id, value: true }));
    commit('SET_FILL_STEP', null);
    commit('SET_FILL_SESSION', { opportunityId, ...(opportunityName ? { opportunityName } : {}) });
    commit('SET_FILL_JOB', {
      jobKey: provisionalKey,
      jobId: null,
      opportunityId,
      formIds: [...formIds],
      completed: 0,
      total: formIds.length,
    });

    return aiModulesService.fillForms({ templateId, opportunityId, documentIds: formIds, signal }).then(
      (job) => {
        const jobId = job && job.job_id;
        if (!jobId) return dispatch('onJobEnd', { opportunityId, jobKey: provisionalKey }).then(() => true);
        if (state.fillJobs[jobId]) {
          // `watchFillJob`/`watchActiveFillJobs` found the queued job before
          // the POST came back and already follows its stream: keep that job
          // as it stands (it may already have released finished forms) and
          // drop the provisional one, releasing the forms no job owns any more.
          const provisional = state.fillJobs[provisionalKey];
          const provisionalFormIds = (provisional && provisional.formIds) || [];
          commit('REMOVE_FILL_JOB', provisionalKey);
          clearFillController(provisionalKey);
          provisionalFormIds
            .filter((formId) => !ownedByAnyJob(state, formId))
            .forEach((formId) => commit('SET_FILLING', { formId, value: false }));
          return true;
        }
        // From here on the job is keyed by its id, controller included.
        commit('RENAME_FILL_JOB', { jobKey: provisionalKey, jobId });
        const controller = fillControllers.get(provisionalKey);
        fillControllers.delete(provisionalKey);
        if (controller) fillControllers.set(jobId, controller);
        return dispatch('subscribeToFillJob', { opportunityId, jobId, signal }).then(() => true);
      },
      (error) =>
        dispatch('onJobEnd', { opportunityId, jobKey: provisionalKey, error: signal.aborted ? null : error }).then(
          () => false,
        ),
    );
  },

  // Follow a running fill job's SSE stream, with the shared poll fallback
  // catching an ending the stream missed.
  subscribeToFillJob({ dispatch }, { opportunityId, jobId, signal }) {
    if (signal.aborted) return Promise.resolve();
    return dispatch('onConnecting', { opportunityId, jobId }).then(() => {
      followLlmJob({
        jobId,
        signal,
        sseCallbacks: fillStreamCallbacks({ dispatch, opportunityId, jobId, signal }),
        onEndedResult: () => dispatch('onJobEnd', { opportunityId, jobKey: jobId }),
      });
    });
  },

  // ---- fill job stream hooks ----
  onConnecting({ commit, rootGetters }, { opportunityId }) {
    const opportunity = rootGetters['opportunity/opportunity'];
    commit('SET_FILL_SESSION', {
      opportunityId,
      ...(opportunity && opportunity.id === opportunityId ? { opportunityName: opportunity.name } : {}),
    });
  },
  // SSE `status` events carry the job step.
  onProgress({ commit }, { ssePayload }) {
    commit('SET_FILL_STEP', ssePayload && ssePayload.step ? ssePayload.step : null);
  },
  // SSE `progress` events: one per finished document, with the job's own
  // completed/total counts.
  onFillProgress({ commit, dispatch, state, rootGetters }, { opportunityId, jobId, ssePayload }) {
    if (!ssePayload) return;
    commit('SET_FILL_JOB', {
      jobKey: jobId,
      ...(ssePayload.completed != null ? { completed: ssePayload.completed } : {}),
      ...(ssePayload.total != null ? { total: ssePayload.total } : {}),
    });
    if (ssePayload.document_id) {
      // This document's fill has ended for this job — release its "filling"
      // state (unless another job is refilling it) and reflect the result on
      // its row right away.
      commit('RELEASE_FORM', { jobKey: jobId, formId: ssePayload.document_id });
      if (!ownedByAnyJob(state, ssePayload.document_id)) {
        commit('SET_FILLING', { formId: ssePayload.document_id, value: false });
      }
      if (isCurrentOpportunity(rootGetters, opportunityId)) {
        commit('APPLY_FILL_RESULT', {
          documentId: ssePayload.document_id,
          fieldsFilled: ssePayload.field_filled_count,
          fieldsTotal: ssePayload.field_count,
          error: ssePayload.error,
        });
        // Fetch the authoritative record in the background (brings the form-fill
        // id needed for download, replaces the optimistic status above).
        dispatch('getDocumentForms', opportunityId).catch(() => {});
      }
    }
  },
  // A job ended (complete / failed / cancelled, live or via the poll
  // fallback): release its forms, drop it, and close the opportunity's session
  // once no job of it is left. Then refresh the page data.
  onJobEnd({ commit, dispatch, state, rootGetters }, { opportunityId, jobKey, error = null }) {
    const job = state.fillJobs[jobKey];
    const formIds = (job && job.formIds) || [];
    commit('REMOVE_FILL_JOB', jobKey);
    clearFillController(jobKey);
    // Only release the forms no other running job still owns.
    formIds
      .filter((formId) => !ownedByAnyJob(state, formId))
      .forEach((formId) => commit('SET_FILLING', { formId, value: false }));
    if (!jobsOf(state, opportunityId).length) {
      commit('SET_FILL_STEP', null);
      commit('CLEAR_FILL_SESSION', opportunityId);
    }
    if (error) commit('ERROR', error);
    if (!isCurrentOpportunity(rootGetters, opportunityId)) return Promise.resolve();
    dispatch('getDocumentForms', opportunityId).catch(() => {});
    return dispatch('opportunity/getOpportunity', { id: opportunityId, silent: true }, { root: true }).catch(() => {});
  },
  // Delete the selected forms by their opportunity document id, then refresh
  // the form-fill records and opportunity so the list reflects the removal.
  deleteForms({ commit, dispatch }, { opportunityId, formIds = [] }) {
    if (!opportunityId || !formIds.length) return Promise.resolve(false);
    return Promise.all(
      formIds.map((documentId) => opportunityDocumentsService.deleteDocument({ opportunityId, documentId })),
    ).then(
      () => {
        // Keep the opportunities list state in sync (document count on the
        // opportunities page), mirroring opportunityDocuments/deleteDocument.
        formIds.forEach((documentId) => {
          commit('opportunities/SUCCESS_DELETING_DOCUMENT', { opportunityId, documentId }, { root: true });
          commit('tenderOpportunities/SUCCESS_DELETING_DOCUMENT', { opportunityId, documentId }, { root: true });
          // The forms page lists the loaded opportunity's documents: drop the
          // row now rather than after the refetch below lands.
          commit('opportunity/SUCCESS_DELETING_DOCUMENT', { opportunityId, documentId }, { root: true });
          commit('REMOVE_FORM_FILL', documentId);
        });
        dispatch('getDocumentForms', opportunityId).catch(() => {});
        return dispatch('opportunity/getOpportunity', { id: opportunityId, silent: true }, { root: true }).then(
          () => true,
        );
      },
      (error) => {
        commit('ERROR', error);
        throw error;
      },
    );
  },
  setOpportunityTemplate({ commit }, { opportunityId, templateId }) {
    commit('SET_OPPORTUNITY_TEMPLATE', { opportunityId, templateId });
  },
};

const mutations = {
  PENDING(state) {
    state.status = { error: null, pending: true, resolved: false, rejected: false };
  },
  SET_FORMS(state, forms) {
    state.forms = forms;
    state.status = { error: null, pending: false, resolved: true, rejected: false };
  },
  ERROR(state, error) {
    state.status = { ...state.status, pending: false, rejected: true, error };
  },
  SET_FORM_STATUS(state, { formId, status }) {
    const form = findForm(state, formId);
    if (form) form.status = status;
  },
  SET_FORM_TEMPLATE(state, { formId, templateId }) {
    const form = findForm(state, formId);
    if (!form) return;
    Vue.set(form, 'templateId', templateId);
  },
  SET_FILLING(state, { formId, value }) {
    if (value) Vue.set(state.filling, formId, true);
    else Vue.delete(state.filling, formId);
  },
  SET_FILL_STEP(state, step) {
    state.fillStep = step;
  },
  // Merges into the existing session (per-opportunity meta such as the name).
  SET_FILL_SESSION(state, { opportunityId, ...session }) {
    const existing = state.fillSessions[opportunityId] || {};
    Vue.set(state.fillSessions, opportunityId, { ...existing, ...session });
  },
  CLEAR_FILL_SESSION(state, opportunityId) {
    Vue.delete(state.fillSessions, opportunityId);
  },
  // Merges into the job so partial updates (e.g. `completed` from a progress
  // event) keep its `formIds`/`opportunityId` intact.
  SET_FILL_JOB(state, { jobKey, ...job }) {
    const existing = state.fillJobs[jobKey] || {};
    Vue.set(state.fillJobs, jobKey, { ...existing, ...job });
  },
  // A job created under a provisional key is re-keyed by its id once known.
  RENAME_FILL_JOB(state, { jobKey, jobId }) {
    const job = state.fillJobs[jobKey];
    if (!job) return;
    Vue.delete(state.fillJobs, jobKey);
    Vue.set(state.fillJobs, jobId, { ...job, jobId });
  },
  REMOVE_FILL_JOB(state, jobKey) {
    Vue.delete(state.fillJobs, jobKey);
  },
  // A job no longer owns a form once that form's fill has ended.
  RELEASE_FORM(state, { jobKey, formId }) {
    const job = state.fillJobs[jobKey];
    if (!job || !job.formIds) return;
    Vue.set(
      job,
      'formIds',
      job.formIds.filter((id) => id !== formId),
    );
  },
  SET_FORM_FILLS(state, formFills) {
    state.formFills = formFills;
  },
  REMOVE_FORM_FILL(state, documentId) {
    state.formFills = state.formFills.filter((f) => f.opportunityDocumentId !== documentId);
  },
  // Optimistic per-document result from a job `progress` event: update (or
  // create) the document's form-fill record with the reported field counts and
  // a derived status, so its row updates as soon as its fill ends. Replaced by
  // the authoritative record on the next `getDocumentForms` fetch.
  APPLY_FILL_RESULT(state, { documentId, fieldsFilled, fieldsTotal, error }) {
    const filled = fieldsFilled || 0;
    const total = fieldsTotal || 0;
    let status = 'NOT_FILLED';
    if (error) status = 'FAILED';
    else if (total > 0 && filled >= total) status = 'FILLED';
    else if (filled > 0) status = 'PARTIALLY_FILLED';

    const index = state.formFills.findIndex((f) => f.opportunityDocumentId === documentId);
    if (index >= 0) {
      const existing = state.formFills[index];
      state.formFills.splice(index, 1, { ...existing, status, fieldsFilled: filled, fieldsTotal: total });
    } else {
      state.formFills.push({ opportunityDocumentId: documentId, status, fieldsFilled: filled, fieldsTotal: total });
    }
  },
  SET_OPPORTUNITY_TEMPLATE(state, { opportunityId, templateId }) {
    Vue.set(state.opportunityTemplates, opportunityId, { templateId });
  },
  RESET(state) {
    clearAllFillControllers();
    Object.assign(state, initialState());
  },
};

const getters = {
  forms: (state) => state.forms,
  formById: (state) => (formId) => findForm(state, formId),
  opportunityTemplate: (state) => (opportunityId) => state.opportunityTemplates[opportunityId] || null,
  // The form-fill record for a document (by opportunityDocumentId), or null.
  formFillByDocument: (state) => (documentId) =>
    state.formFills.find((f) => f.opportunityDocumentId === documentId) || null,
  filledCountFor: (state, getters) => (documentId) => {
    const fill = getters.formFillByDocument(documentId);
    return fill ? { filled: fill.fieldsFilled || 0, total: fill.fieldsTotal || 0 } : { filled: 0, total: 0 };
  },
  // A document is "filling" while its live SSE fill session is running (tracked
  // in memory). The backend record has no in-progress status to fall back on.
  isFilling: (state) => (formId) => !!state.filling[formId],
  // Whether a fill is currently running for a specific opportunity — scoped per
  // opportunity so one opportunity's fill doesn't light up another's forms page.
  anyFillingFor: (state) => (opportunityId) => jobsOf(state, opportunityId).length > 0,
  fillStep: (state) => state.fillStep,
  // Live { completed, total } document counts over an opportunity's running
  // fill jobs (two "Fill" clicks are two jobs, shown as one count), or null.
  fillSessionFor: (state) => (opportunityId) => {
    const jobs = jobsOf(state, opportunityId);
    if (!jobs.length) return null;
    return {
      ...(state.fillSessions[opportunityId] || {}),
      completed: jobs.reduce((sum, job) => sum + (job.completed || 0), 0),
      total: jobs.reduce((sum, job) => sum + (job.total || 0), 0),
    };
  },
  // Running fills as header task entries — one per opportunity, with the
  // documents-filled counts driving the task's percentage.
  fillTasks: (state, getters) =>
    [...new Set(Object.keys(state.fillJobs).map((key) => state.fillJobs[key].opportunityId))].map((opportunityId) => {
      const session = getters.fillSessionFor(opportunityId);
      return {
        opportunityId,
        opportunityName: session.opportunityName || null,
        completed: session.completed,
        total: session.total,
      };
    }),
  pending: (state) => state.status.pending,
  resolved: (state) => state.status.resolved,
  rejected: (state) => state.status.rejected,
  error: (state) => state.status.error,
};

export const formFill = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
